// src/components/ExperienceHighlights.tsx
//
// Home page "Experience across real systems".
//
// Visual proof that the work has been on real enterprise systems, without
// turning Home into a project portfolio. There are no project names, no dates
// and no per-system links: the resume documents the position at Centroxy
// rather than the individual engagements, so dating them would be a
// fabrication. The full account lives on /about, which the CTA points to.
//
// Layout is a connected spine of four domain nodes - horizontal on desktop,
// vertical on mobile - with a pulse travelling along each connector so the row
// reads as systems exchanging data rather than a list.
//
// This section has no button of its own. It used to end with "Explore My
// Experience" pointing at /about, two sections below the snapshot's "Read My
// Experience" pointing at the same route. The lede now carries that pointer,
// so /about is offered once as a hard CTA and once as a sentence.
import { Fragment, useCallback, useRef, useState, type ComponentType, type KeyboardEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { FaBolt, FaPlaneDeparture, FaSitemap, FaWifi } from 'react-icons/fa';

import { homeConfig } from '../config/homeConfig';
import type { HomeHighlightIcon, HomeHighlightItem } from '../config/homeConfig';
import { minDevice } from '../styles/breakpoints';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { STAGGER, motionSet } from './about/motion';
import {
  HighlightsRoot,
  HighlightsContainer,
  Header,
  Eyebrow,
  Heading,
  Lede,
  Spine,
  Node,
  NodeButton,
  NodeDot,
  NodeIndex,
  NodeLabel,
  Connector,
  ConnectorTrack,
  ConnectorPulse,
  PanelWrap,
  DetailPanel,
  ClientRow,
  ClientName,
  DetailTitle,
  DetailDescription,
  FocusLabel,
  FocusList,
  FocusItem,
  StackNote,
} from './ExperienceHighlights.styles';

const headingId = 'home-highlights-heading';

// One stable id, so every tab's aria-controls resolves. A per-tab id would
// dangle for the three inactive nodes.
const panelId = 'home-highlight-panel';

const viewport = { once: true, amount: 0.3 } as const;

// Same four icons the About page uses for the same four domains, so the two
// pages read as one system.
const iconMap: Record<HomeHighlightIcon, ComponentType> = {
  aviation: FaPlaneDeparture,
  iot: FaWifi,
  asset: FaSitemap,
  energy: FaBolt,
};

const Detail = ({ item, focusLabel }: { item: HomeHighlightItem; focusLabel: string }) => (
  <DetailPanel>
    <ClientRow>
      <span>{item.index}</span>
      {item.client && (
        <>
          <span aria-hidden="true">/</span>
          <ClientName>{item.client}</ClientName>
        </>
      )}
    </ClientRow>
    <DetailTitle>{item.domain}</DetailTitle>
    <DetailDescription>{item.description}</DetailDescription>
    <FocusLabel>{focusLabel}</FocusLabel>
    <FocusList>
      {item.focus.map((entry) => (
        <FocusItem key={entry}>{entry}</FocusItem>
      ))}
    </FocusList>
  </DetailPanel>
);

const ExperienceHighlights = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const isDesktop = useMediaQuery(minDevice.laptop);

  const labels = homeConfig.highlights;
  const { items } = labels;
  const anim = motionSet(reduceMotion);

  const [activeId, setActiveId] = useState(items[0].id);
  const nodeRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const active = items.find((item) => item.id === activeId) ?? items[0];

  const select = useCallback((id: string) => setActiveId(id), []);

  const move = (from: number, by: number) => {
    const target = (from + by + items.length) % items.length;
    select(items[target].id);
    nodeRefs.current[target]?.focus();
  };

  // ARIA APG: Left/Right for a horizontal tablist, Up/Down for a vertical one.
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const forward = isDesktop ? 'ArrowRight' : 'ArrowDown';
    const backward = isDesktop ? 'ArrowLeft' : 'ArrowUp';

    if (event.key === forward) {
      event.preventDefault();
      move(index, 1);
    } else if (event.key === backward) {
      event.preventDefault();
      move(index, -1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      move(0, 0);
    } else if (event.key === 'End') {
      event.preventDefault();
      move(items.length - 1, 0);
    }
  };

  const pulseTransition = (index: number) => ({
    duration: 2.4,
    ease: 'easeInOut' as const,
    repeat: Infinity,
    // Stagger the start so the pulses read as traffic, not a single blink.
    delay: index * 0.45,
  });

  return (
    <HighlightsRoot aria-labelledby={headingId}>
      <HighlightsContainer>
        <Header
          variants={anim.group(STAGGER.group)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          <Eyebrow variants={anim.reveal}>{labels.eyebrow}</Eyebrow>
          <Heading id={headingId} variants={anim.reveal}>
            {labels.heading}
          </Heading>
          <Lede variants={anim.reveal}>{labels.lede}</Lede>
        </Header>

        <Spine
          role="tablist"
          aria-orientation={isDesktop ? 'horizontal' : 'vertical'}
          aria-label={labels.listLabel}
        >
          {items.map((item, index) => {
            const Icon = iconMap[item.icon];
            const $active = item.id === active.id;
            const isLast = index === items.length - 1;

            return (
              <Fragment key={item.id}>
                <Node>
                  <NodeButton
                    ref={(node) => {
                      nodeRefs.current[index] = node;
                    }}
                    type="button"
                    role="tab"
                    id={`highlight-tab-${item.id}`}
                    aria-selected={$active}
                    aria-controls={panelId}
                    tabIndex={$active ? 0 : -1}
                    $active={$active}
                    onClick={() => select(item.id)}
                    onMouseEnter={() => select(item.id)}
                    onFocus={() => select(item.id)}
                    onKeyDown={(event) => handleKeyDown(event, index)}
                  >
                    <NodeDot $active={$active}>
                      <Icon aria-hidden="true" />
                    </NodeDot>
                    <NodeIndex>{item.index}</NodeIndex>
                    <NodeLabel $active={$active}>{item.shortLabel}</NodeLabel>
                  </NodeButton>
                </Node>

                {/* aria-hidden keeps the decorative connector out of the a11y
                    tree, so the tablist still exposes only tabs. */}
                {!isLast && (
                  <Connector aria-hidden="true">
                    <ConnectorTrack />
                    {!reduceMotion && (
                      <ConnectorPulse
                        initial={false}
                        animate={isDesktop ? { left: ['0%', '100%'] } : { top: ['0%', '100%'] }}
                        transition={pulseTransition(index)}
                      />
                    )}
                  </Connector>
                )}
              </Fragment>
            );
          })}
        </Spine>

        <PanelWrap>
          <div role="tabpanel" id={panelId} aria-labelledby={`highlight-tab-${active.id}`}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div key={active.id} variants={anim.panel} initial="hidden" animate="visible" exit="exit">
                <Detail item={active} focusLabel={labels.focusLabel} />
              </motion.div>
            </AnimatePresence>
          </div>
        </PanelWrap>

        <StackNote variants={anim.reveal} initial="hidden" whileInView="visible" viewport={viewport}>
          <strong>Applied across all four systems</strong> — {labels.stackNote}
        </StackNote>
      </HighlightsContainer>
    </HighlightsRoot>
  );
};

export default ExperienceHighlights;
